import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader, Trash2 } from 'lucide-react';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { getApiUrl } from '../utils/apiHelpers';
import MarketingLayout from './marketing/MarketingLayout';
import { Article, ArticleHeader, Section, Callout } from './marketing/Article';

const EFFECTIVE_DATE = 'September 2, 2026';

// Public account-deletion instructions page. Google Play requires a URL on the
// store listing that names the app, prominently shows the steps to request
// deletion, and specifies what data is deleted or kept and for how long.
// It also lets the user run the workflow right here: sign in (bouncing back to
// this page via /login?next=), then type DELETE to confirm.
function DeleteAccountPage({ user, isLoading }) {
  return (
    <MarketingLayout>
      <Article>
        <ArticleHeader title="Delete your account">
          <p className="text-sm text-[var(--color-ink-light)] font-serif italic">
            For Steadily.me, published by Reality Moss LLC &middot; Last updated: {EFFECTIVE_DATE}
          </p>
        </ArticleHeader>

        <Callout title="At a glance" className="mt-10 mb-12">
          <p>
            You can delete your Steadily.me account and its data at any time, either from inside the app or by
            emailing us. Deletion is confirmed by typing <strong>DELETE</strong> while signed in and is permanent.
            Once confirmed, your habits, completions, and account information are removed from our active systems
            immediately.
          </p>
        </Callout>

        <DeletionWorkflow user={user} isLoading={isLoading} />

        <div className="space-y-10 text-[var(--color-ink-main)] leading-relaxed">
          <Section title="How to delete your account from the app">
            <p>If you can sign in to Steadily.me (the mobile app, desktop app, or the web app at steadily.me):</p>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Sign in and open <strong>Settings</strong>.</li>
              <li>Go to <strong>Account</strong> and choose <strong>Delete account</strong>.</li>
              <li>
                Type <strong>DELETE</strong> in the confirmation box and confirm. Your account and data are deleted
                immediately.
              </li>
            </ol>
            <p>
              If you signed in with Apple, deleting your account also revokes Steadily.me's Sign in with Apple
              access, so the app disappears from your Apple ID's sign-in list.
            </p>
          </Section>

          <Section title="How to request deletion by email">
            <p>
              If you can't access the app, for example because you've uninstalled it or lost access to a device, you can
              request deletion by email instead:
            </p>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                Email <a href="mailto:support@steadily.me" className="underline">support@steadily.me</a> with the
                subject line <strong>"Delete my account."</strong>
              </li>
              <li>
                Send the request from the email address your Steadily.me account is registered under, so we can
                verify it's really you.
              </li>
              <li>We'll confirm and complete the deletion, normally within a few business days.</li>
            </ol>
            <p>
              You can also use this address to ask us to delete specific data (for example, your support conversation
              history) without closing your whole account.
            </p>
          </Section>

          <Section title="What is deleted">
            <p>When your deletion is confirmed, we permanently delete:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your account information: email address, sign-in identifiers (including Google or Apple sign-in), and password hash.</li>
              <li>All your habit content: habits, completion history, and sick/rest weeks.</li>
              <li>Your synced device records.</li>
              <li>Your support conversations and messages.</li>
              <li>Error-telemetry logs tied to your account, and any pending email-confirmation or password-reset tokens.</li>
            </ul>
            <p>
              Deletion from our active systems happens immediately on confirmation; routine encrypted backups may
              retain a copy for up to an additional 60 days before they are overwritten.
            </p>
          </Section>

          <Section title="What is kept, and for how long">
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>An anonymous deletion statistic.</strong> We keep a small anonymous record that an account
                was deleted: when it was created and deleted, and aggregate usage counts (such as the number of
                habits it had). We use it to understand why people leave. It contains no email address or other personal
                information, so it cannot be linked back to you.
              </li>
              <li>
                <strong>Anonymized log entries.</strong> When you delete your account, its activity log entries
                (sign-ins, syncs, and similar events) are anonymized immediately: the link to your account, the IP
                address, user agent, city, and event details are removed, leaving only the type of event, its date, the
                device type, and the country for aggregate statistics. Server error logs and security events lose the
                link to your account in the same step. The remaining anonymous entries are deleted automatically on the
                schedules in our <Link to="/privacy" className="underline">Privacy Policy</Link>: activity entries after
                365 days, and error and security records after 30 to 365 days.
              </li>
              <li>
                <strong>Purchase records.</strong> If you bought Steadily.me Pro, the payment record is held by the
                Apple App Store, Google Play, or Stripe (and purchase metadata by RevenueCat) under their own
                policies; we can't delete those on their systems. Deleting your account does <strong>not</strong>{' '}
                cancel an active subscription; cancel it in your App Store or Google Play subscription settings, or
                through the "Manage subscription" link for web checkout purchases, before you delete.
              </li>
            </ul>
          </Section>

          <Section title="Questions">
            <p>
              If anything here is unclear, or you run into trouble deleting your account, email
              {' '}<a href="mailto:support@steadily.me" className="underline">support@steadily.me</a> and we'll help.
              For full details on how we handle your data, see our{' '}
              <Link to="/privacy" className="underline">Privacy Policy</Link>.
            </p>
            <p className="text-sm text-[var(--color-ink-light)] mt-4">
              Reality Moss LLC<br />
              An Ohio limited liability company
            </p>
          </Section>
        </div>
      </Article>
    </MarketingLayout>
  );
}

// Interactive card that walks the user through the deletion workflow:
// 1. sign in (via /login?next=/delete-account), 2. type DELETE and confirm.
// Deletion is immediate and permanent, no email round-trip.
function DeletionWorkflow({ user, isLoading }) {
  // 'idle' | 'deleting' | 'deleted'
  const [deleteState, setDeleteState] = useState('idle');
  const [deleteInput, setDeleteInput] = useState('');
  const [error, setError] = useState('');

  const signedIn = !!user;
  const deleted = deleteState === 'deleted';

  const deleteAccount = async () => {
    if (deleteInput !== 'DELETE') {
      setError('Please type "DELETE" exactly to confirm');
      return;
    }
    setDeleteState('deleting');
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithRetry(`${getApiUrl()}/api/auth/delete-account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirmation: 'DELETE' })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete your account');
      }
      // The account is gone, so the stored session is dead too.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setDeleteState('deleted');
    } catch (err) {
      setError(err.message);
      setDeleteState('idle');
    }
  };

  return (
    <section className="mt-2 mb-12 p-6 bg-[var(--color-paper-warm)] border border-[var(--color-divider)] rounded-2xl">
      <h2 className="font-serif font-bold text-lg mb-4">Delete your account here</h2>

      {isLoading ? (
        <div className="flex items-center gap-3 text-[var(--color-ink-light)] font-serif">
          <Loader className="w-4 h-4 animate-spin" />
          <span>Checking whether you're signed in…</span>
        </div>
      ) : deleted ? (
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-600 border border-green-600 text-white flex items-center justify-center">
            <Check className="w-4 h-4" />
          </div>
          <div className="pt-1">
            <h3 className="font-serif font-semibold text-base mb-1">Account deleted</h3>
            <p className="leading-relaxed">
              Your account and all associated data have been permanently deleted. We're sorry to see you go.
            </p>
          </div>
        </div>
      ) : (
        <ol className="space-y-5">
          <WorkflowStep
            number={1}
            title="Sign in"
            state={signedIn ? 'done' : 'active'}
          >
            {signedIn ? (
              <p>Signed in as <strong>{user.email}</strong>.</p>
            ) : (
              <>
                <p className="mb-3">
                  Sign in so we know which account to delete. You'll be brought straight back to this page.
                </p>
                <Link
                  to="/login?next=/delete-account"
                  className="inline-block px-5 py-2.5 bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] rounded-xl hover:shadow-lg transition-all font-medium font-serif"
                >
                  Sign in to continue
                </Link>
                <p className="mt-3 text-sm text-[var(--color-ink-light)]">
                  Can't sign in any more? Email us instead, using the steps below.
                </p>
              </>
            )}
          </WorkflowStep>

          <WorkflowStep
            number={2}
            title="Type DELETE and confirm"
            state={signedIn ? 'active' : 'upcoming'}
          >
            {!signedIn && <p>Available once you're signed in.</p>}
            {signedIn && (
              <>
                <p className="mb-3">
                  This permanently deletes <strong>{user.email}</strong> and all its data, immediately. There is no
                  way to undo it.
                </p>
                <input
                  type="text"
                  placeholder="Type DELETE"
                  value={deleteInput}
                  onChange={(e) => {
                    setDeleteInput(e.target.value);
                    setError('');
                  }}
                  disabled={deleteState === 'deleting'}
                  className="w-full max-w-xs px-4 py-2.5 mb-3 bg-[var(--color-paper-bg)] text-[var(--color-ink-main)] rounded-xl border border-[var(--color-divider)] focus:border-red-500 focus:outline-none transition-colors font-serif"
                />
                <div>
                  <button
                    onClick={deleteAccount}
                    disabled={deleteInput !== 'DELETE' || deleteState === 'deleting'}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium font-serif transition-all ${
                      deleteInput !== 'DELETE' || deleteState === 'deleting'
                        ? 'bg-[var(--color-divider)] text-[var(--color-ink-subtle)] cursor-not-allowed'
                        : 'bg-red-600 text-white hover:shadow-lg'
                    }`}
                  >
                    {deleteState === 'deleting' ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Deleting…
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Permanently delete my account
                      </>
                    )}
                  </button>
                </div>
                {error && (
                  <p className="mt-3 text-sm text-red-600">
                    {error}. Please try again, or email{' '}
                    <a href="mailto:support@steadily.me" className="underline">support@steadily.me</a>.
                  </p>
                )}
              </>
            )}
          </WorkflowStep>
        </ol>
      )}
    </section>
  );
}

function WorkflowStep({ number, title, state, children }) {
  const circle =
    state === 'done'
      ? 'bg-green-600 border-green-600 text-white'
      : state === 'active'
        ? 'bg-[var(--color-ink-main)] border-[var(--color-ink-main)] text-[var(--color-paper-bg)]'
        : 'bg-transparent border-[var(--color-divider)] text-[var(--color-ink-subtle)]';

  return (
    <li className={`flex gap-4 ${state === 'upcoming' ? 'opacity-60' : ''}`}>
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center font-serif font-bold text-sm ${circle}`}
        aria-hidden="true"
      >
        {state === 'done' ? <Check className="w-4 h-4" /> : number}
      </div>
      <div className="min-w-0 pt-0.5">
        <h3 className="font-serif font-semibold text-base mb-1">
          {title}
          {state === 'done' && <span className="sr-only"> (complete)</span>}
        </h3>
        <div className="text-[var(--color-ink-main)] leading-relaxed">{children}</div>
      </div>
    </li>
  );
}

export default DeleteAccountPage;
